// Lightweight typed fetch wrapper for the Lexicon REST API.
// In dev, vite proxies /api/* to wrangler. In prod, same origin.

import type {
  Paged, Domain, RecordRow, RecordVersion, ApprovalRequest, Hierarchy,
  MappingSet, MappingItem, LineageGraph, QualityRule, QualityScore,
  ImportJob, ApiKey, Webhook, Integration, AuditEvent, SearchHit, ApiError,
} from "./types";

const BASE = "/api";

class HttpError extends Error {
  constructor(public status: number, public body: ApiError) {
    super(body.message ?? `HTTP ${status}`);
  }
}

function isMutation(method?: string): boolean {
  if (!method) return false;
  const m = method.toUpperCase();
  return m === "POST" || m === "PATCH" || m === "PUT" || m === "DELETE";
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "content-type": "application/json", accept: "application/json" },
    credentials: "include",
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: "unknown", message: res.statusText }))) as ApiError;
    throw new HttpError(res.status, body);
  }
  if (isMutation(init?.method) && typeof window !== "undefined") {
    try { window.dispatchEvent(new CustomEvent("aidan:mutation", { detail: { path, method: init?.method } })); } catch { /* ignore */ }
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// Query string builder
function qs(params: Record<string, unknown> = {}): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === "") continue;
    if (Array.isArray(v)) v.forEach(item => sp.append(k, String(item)));
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const api = {
  // Domains
  domains: {
    list: () => request<Domain[]>("/domains"),
    get: (id: string) => request<Domain>(`/domains/${id}`),
    schema: (id: string) => request<{ fields: Array<{ key: string; label: string; type: string }> }>(`/domains/${id}/schema`),
    create: (body: {
      id?: string; name: string; description?: string; icon?: string; owner?: string; primaryKey?: string;
      fields?: Array<{ key: string; label: string; type?: string; required?: boolean; enum?: string[]; refDomain?: string; description?: string; searchable?: boolean; sortOrder?: number }>;
    }) => request<Domain>("/domains", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: Partial<{ name: string; description: string; icon: string; owner: string }>) =>
      request<{ ok: true }>(`/domains/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) => request<{ ok: true }>(`/domains/${id}`, { method: "DELETE" }),
    setSchema: (id: string, fields: Array<{
      key: string; label?: string; type?: string; required?: boolean;
      enum?: string[]; refDomain?: string; description?: string;
      searchable?: boolean; sortOrder?: number;
    }>) => request<{ ok: true; fieldCount: number }>(
      `/domains/${id}/schema`,
      { method: "PUT", body: JSON.stringify({ fields }) },
    ),
  },

  // Records
  records: {
    list: (domainId: string, params: { q?: string; page?: number; pageSize?: number; sort?: string; filter?: Record<string, string> } = {}) =>
      request<Paged<RecordRow>>(`/domains/${domainId}/records${qs(params)}`),
    get: (domainId: string, id: string) => request<RecordRow>(`/domains/${domainId}/records/${id}`),
    create: (domainId: string, data: Record<string, unknown>) =>
      request<RecordRow>(`/domains/${domainId}/records`, { method: "POST", body: JSON.stringify(data) }),
    update: (domainId: string, id: string, data: Record<string, unknown>) =>
      request<RecordRow>(`/domains/${domainId}/records/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    remove: (domainId: string, id: string) =>
      request<void>(`/domains/${domainId}/records/${id}`, { method: "DELETE" }),
    export: (domainId: string, format: "csv" | "json" | "xlsx" = "csv", filter: Record<string, string> = {}) =>
      `${BASE}/domains/${domainId}/records/export${qs({ format, ...filter })}`,
  },

  // Versions
  versions: {
    list: (domainId: string, recordId: string) => request<RecordVersion[]>(`/domains/${domainId}/records/${recordId}/versions`),
    get: (domainId: string, recordId: string, v: number) =>
      request<RecordVersion>(`/domains/${domainId}/records/${recordId}/versions/${v}`),
    diff: (domainId: string, recordId: string, a: number, b: number) =>
      request<{ diff: Array<{ key: string; before: unknown; after: unknown }> }>(`/domains/${domainId}/records/${recordId}/versions/diff${qs({ a, b })}`),
    restore: (domainId: string, recordId: string, v: number) =>
      request<RecordRow>(`/domains/${domainId}/records/${recordId}/versions/${v}/restore`, { method: "POST" }),
  },

  // Approvals
  approvals: {
    list: (params: { status?: string; priority?: string; assignee?: string; page?: number; pageSize?: number } = {}) =>
      request<Paged<ApprovalRequest>>(`/approvals${qs(params)}`),
    get: (id: string) => request<ApprovalRequest>(`/approvals/${id}`),
    approve: (id: string, comment?: string) =>
      request<ApprovalRequest>(`/approvals/${id}/approve`, { method: "POST", body: JSON.stringify({ comment }) }),
    reject: (id: string, comment: string) =>
      request<ApprovalRequest>(`/approvals/${id}/reject`, { method: "POST", body: JSON.stringify({ comment }) }),
    bulkApprove: (ids: string[]) =>
      request<{ ok: number; fail: number }>(`/approvals/bulk-approve`, { method: "POST", body: JSON.stringify({ ids }) }),
    comment: (id: string, body: string) =>
      request<void>(`/approvals/${id}/comments`, { method: "POST", body: JSON.stringify({ body }) }),
    pendingCount: () => request<{ count: number }>(`/approvals/pending/count`),
  },

  // Hierarchies
  hierarchies: {
    list: (domainId?: string) => request<Hierarchy[]>(`/hierarchies${qs({ domainId })}`),
    get: (id: string) => request<Hierarchy>(`/hierarchies/${id}`),
    reparent: (id: string, nodeId: string, newParentId: string | null) =>
      request<void>(`/hierarchies/${id}/reparent`, { method: "POST", body: JSON.stringify({ nodeId, newParentId }) }),
    addNode: (id: string, body: { label: string; parentId?: string | null; badge?: string }) =>
      request<{ id: string; hierarchyId: string; parentId?: string; label: string; badge?: string }>(
        `/hierarchies/${id}/nodes`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    renameNode: (id: string, nodeId: string, label: string) =>
      request<void>(`/hierarchies/${id}/nodes/${nodeId}`, { method: "PATCH", body: JSON.stringify({ label }) }),
    removeNode: (id: string, nodeId: string) =>
      request<void>(`/hierarchies/${id}/nodes/${nodeId}`, { method: "DELETE" }),
  },

  // Mappings
  mappings: {
    sets: () => request<MappingSet[]>(`/mappings`),
    items: (setId: string, params: { q?: string; minConfidence?: number; page?: number; pageSize?: number } = {}) =>
      request<Paged<MappingItem>>(`/mappings/${setId}/items${qs(params)}`),
    verify: (setId: string, itemId: string) =>
      request<MappingItem>(`/mappings/${setId}/items/${itemId}/verify`, { method: "POST" }),
    updateItem: (setId: string, itemId: string, body: { targetId?: string; confidence?: number; notes?: string }) =>
      request<MappingItem>(`/mappings/${setId}/items/${itemId}`, { method: "PATCH", body: JSON.stringify(body) }),
    autoMap: (setId: string) =>
      request<{ mapped: number }>(`/mappings/${setId}/auto-map`, { method: "POST" }),
  },

  // Lineage
  lineage: {
    forRecord: (domainId: string, recordId: string, depth = 2) =>
      request<LineageGraph>(`/lineage/${domainId}/${recordId}${qs({ depth })}`),
    get: (domainId: string, recordId: string, depth = 2) =>
      request<LineageGraph>(`/lineage/${domainId}/${recordId}${qs({ depth })}`),
    impact: (domainId: string, recordId: string) =>
      request<{ affected: number; preview: Array<{ id: string; label: string }> }>(`/lineage/${domainId}/${recordId}/impact`),
  },

  // Imports/Exports
  imports: {
    list: () => request<ImportJob[]>(`/imports`),
    get: (id: string) => request<ImportJob>(`/imports/${id}`),
    upload: async (domainId: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      form.append("domainId", domainId);
      const res = await fetch(`${BASE}/imports`, { method: "POST", body: form, credentials: "include" });
      if (!res.ok) throw new Error("upload failed");
      return res.json() as Promise<ImportJob>;
    },
    confirmMapping: (id: string, mapping: Record<string, string>) =>
      request<ImportJob>(`/imports/${id}/confirm`, { method: "POST", body: JSON.stringify({ mapping }) }),
    cancel: (id: string) => request<void>(`/imports/${id}/cancel`, { method: "POST" }),
  },

  // Quality
  quality: {
    rules: (domainId?: string) => request<QualityRule[]>(`/quality/rules${qs({ domainId })}`),
    score: (domainId: string) => request<QualityScore>(`/quality/score/${domainId}`),
    runRule: (ruleId: string) => request<{ pass: number; fail: number }>(`/quality/rules/${ruleId}/run`, { method: "POST" }),
    runAll: () => request<{ rules: number; pass: number; fail: number }>(`/quality/rules/run-all`, { method: "POST" }),
    createRule: (body: { domainId: string; name: string; description?: string; expression: string; severity?: string; enabled?: boolean }) =>
      request<QualityRule>(`/quality/rules`, { method: "POST", body: JSON.stringify(body) }),
    updateRule: (id: string, body: { name?: string; description?: string; expression?: string; severity?: string; enabled?: boolean }) =>
      request<{ ok: boolean }>(`/quality/rules/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    violations: (params: { domainId?: string; limit?: number } = {}) =>
      request<Array<{ recordId: string; domainId: string; ruleId: string; ruleName: string; severity: string; evaluatedAt: string }>>(
        `/quality/violations${qs(params)}`,
      ),
  },

  // Integrations
  integrations: {
    list: () => request<Integration[]>(`/integrations`),
    syncLogs: (limit = 25) =>
      request<Array<{ id: string; at: string; direction: "in" | "out"; source: string; event: string; status: number; bytes: number }>>(
        `/integrations/sync-logs${qs({ limit })}`,
      ),
    apiKeys: {
      list: () => request<ApiKey[]>(`/integrations/api-keys`),
      create: (name: string, scopes: string[]) =>
        request<{ key: string; meta: ApiKey }>(`/integrations/api-keys`, { method: "POST", body: JSON.stringify({ name, scopes }) }),
      revoke: (id: string) => request<void>(`/integrations/api-keys/${id}`, { method: "DELETE" }),
    },
    webhooks: {
      list: () => request<Webhook[]>(`/integrations/webhooks`),
      create: (url: string, events: string[]) =>
        request<Webhook>(`/integrations/webhooks`, { method: "POST", body: JSON.stringify({ url, events }) }),
      remove: (id: string) => request<void>(`/integrations/webhooks/${id}`, { method: "DELETE" }),
      test: (id: string) =>
        request<{ ok: boolean; status: number; ms: number }>(`/integrations/webhooks/${id}/test`, { method: "POST" }),
    },
  },

  // Generic explorer call (used by API Explorer UI)
  raw: async (method: string, path: string): Promise<{ status: number; body: unknown }> => {
    const res = await fetch(`${BASE}${path.startsWith("/api") ? path.slice(4) : path}`, {
      method,
      headers: { accept: "application/json" },
      credentials: "include",
    });
    let body: unknown;
    try { body = await res.json(); } catch { body = await res.text(); }
    return { status: res.status, body };
  },

  // Audit
  audit: {
    list: (params: { actor?: string; action?: string; from?: string; to?: string; page?: number } = {}) =>
      request<Paged<AuditEvent>>(`/audit${qs(params)}`),
  },

  // Global search
  search: (q: string, params: { domain?: string; limit?: number } = {}) =>
    request<SearchHit[]>(`/search${qs({ q, ...params })}`),

  // Dashboard overview (aggregates)
  stats: {
    overview: () => request<{
      totals: { records: number; domains: number; pendingApprovals: number; qualityScore: number; pendingDelta: number };
      perDomainQuality: Array<{ domainId: string; score: number; count: number }>;
      datasets: Array<{ id: string; name: string; icon: string; owner: string; count: number; updatedAt: string }>;
      activity: Array<{ id: string; actor: string; action: string; target: string; at: string }>;
      pending: Array<{ id: string; domainId: string; recordId: string; summary: string; maker: string; priority: string; at: string }>;
    }>(`/stats/overview`),
  },
};

export { HttpError };
